import { useEffect, useState } from 'react';
import { REDUCERS } from '@/constants/reducers';
import { SITE_USER_COOKIE } from '@/constants/site';
import { SUBSCRIPTION_TYPE } from '@/constants/subscription-type';
import {
    addGoogleTagManagerAndAnalytics,
    addStpdScript,
    addHbAgencyScript,
    addIntersitialVideooTvScript,
} from '@/helpers/ads-helper';
import { getFromCookies } from '@/helpers/cookies';

export default function useConsentManager(dispatch) {
    const [cookiesAccepted, setCookiesAccepted] = useState(false);
    const [scriptsLoaded, setScriptsLoaded] = useState(false);

    useEffect(() => {
        const checkConsent = () => {
            if (typeof window !== 'undefined' && window.__tcfapi) {
                window.__tcfapi('getTCData', 2, (tcData, success) => {
                    const consentGiven = success && tcData.purpose?.consents?.[1] === true;
                    setCookiesAccepted(consentGiven);
                    if (consentGiven) {
                        dispatch({ type: REDUCERS.UPDATE_APP, payload: { cookiesAccepted: true } });
                    }
                });
            }
        };

        const handleConsentChange = (tcData, success) => {
            const consentGiven =
                success &&
                tcData.eventStatus === 'useractioncomplete' &&
                tcData.purpose?.consents?.[1] === true;

            if (consentGiven) {
                setCookiesAccepted(true);
                dispatch({ type: REDUCERS.UPDATE_APP, payload: { cookiesAccepted: true } });
            }
        };

        checkConsent();

        if (typeof window !== 'undefined' && window.__tcfapi) {
            window.__tcfapi('addEventListener', 2, handleConsentChange);
        }

        return () => {
            if (typeof window !== 'undefined' && window.__tcfapi) {
                window.__tcfapi('removeEventListener', 2, handleConsentChange);
            }
        };
    }, [dispatch]);

    useEffect(() => {
        const STORAGE_KEY = 'site_visit_count';
        const MIN_VISITS_FOR_INTERSTITIAL = 3;

        // Incrementar contador de visitas
        const incrementVisitCount = () => {
            try {
                if (typeof window === 'undefined') return 0;

                const stored = localStorage.getItem(STORAGE_KEY);
                let visitCount = 0;

                if (stored) {
                    const parsed = parseInt(stored, 10);
                    visitCount = isNaN(parsed) ? 0 : parsed;
                }

                visitCount += 1;
                localStorage.setItem(STORAGE_KEY, visitCount.toString());
                return visitCount;
            } catch (error) {
                console.error('Error al incrementar contador de visitas:', error);
                return 0;
            }
        };

        const addAdScripts = () => {
            const isProduction = process.env.NEXT_PUBLIC_ENV_VARIABLE !== 'development';
            if (!isProduction || scriptsLoaded) return;

            // Incrementar contador de visitas
            const visitCount = incrementVisitCount();

            const dataFromCookies = getFromCookies(SITE_USER_COOKIE);
            let data = null;
            try {
                data = JSON.parse(dataFromCookies);
            } catch (e) {
                console.error('Error parsing site user cookie', e);
            }

            const isFree = !data || data.subscriptionType === SUBSCRIPTION_TYPE.FREE;

            if (isFree) {
                // Solo agregar el script intersticial si tiene 3 o más visitas
                if (visitCount >= MIN_VISITS_FOR_INTERSTITIAL) {
                    addIntersitialVideooTvScript();
                }
                addStpdScript();
                addHbAgencyScript();
            }

            addGoogleTagManagerAndAnalytics();
            setScriptsLoaded(true);
        };

        if (!scriptsLoaded) {
            addAdScripts();
        }

        if (cookiesAccepted) {
            dispatch({ type: REDUCERS.UPDATE_COOKIES_ACCEPTED, payload: true });
        }
    }, [cookiesAccepted, scriptsLoaded]);

    return { cookiesAccepted, setCookiesAccepted };
}